import "./style.scss";
import { IonCol, IonGrid, IonPicker, IonRow, IonText } from "@ionic/react";
import { Button, SxProps } from "@mui/material";
import { FC, useState } from "react";

import {
  convertToUtcFormat,
  extractLocalTimeInformation,
  generateTimePickerOptions,
} from "./helper";
import { ITimeStepperProps, Meridiem, ShiftTiming } from "./interfaces";

function getDisplayText(timeInUtc, timezone) {
  const { hours, minutes, meridiem } = extractLocalTimeInformation(timeInUtc, timezone);
  return `${hours}:${minutes} ${meridiem.toUpperCase()}`;
}

/**
 * @deprecated
 * Old Styles. We should not override standard button styles.
 */
const DEPRECATED_EDIT_SHIFT_TIME_BUTTON_SX: SxProps = {
  fontSize: "32px",
  textDecoration: "underline",
  paddingRight: 0,
};

export const TimeStepper: FC<ITimeStepperProps> = ({
  facilityTimezone,
  currentStart,
  currentEnd,
  newStartTime,
  newEndTime,
  setNewStartTime,
  setNewEndTime,
  disabled,
}) => {
  const [currentTiming, setCurrentTiming] = useState<ShiftTiming>(ShiftTiming.START);
  const [showPicker, setShowPicker] = useState<boolean>(false);

  const startDisplayText = getDisplayText(
    newStartTime.length ? newStartTime : currentStart,
    facilityTimezone
  );

  const endDisplayText = getDisplayText(
    newEndTime.length ? newEndTime : currentEnd,
    facilityTimezone
  );

  const openTimeEditor = (shiftTiming: ShiftTiming) => {
    setShowPicker(true);
    setCurrentTiming(shiftTiming);
  };

  const handleTimeChange = (event) => {
    const hours = event.hour.text;
    const minutes = event.minute.text;
    const meridiem = event.meridiem.text;
    const newTimeProps = {
      timezone: facilityTimezone,
      hoursIn12hourFormat: hours,
      minutes,
      meridiem,
    };
    if (currentTiming === ShiftTiming.START) {
      const { year, month, date } = extractLocalTimeInformation(currentStart, facilityTimezone);
      setNewStartTime(
        convertToUtcFormat({
          ...newTimeProps,
          year,
          month,
          date,
        })
      );
    } else {
      const { year, month, date } = extractLocalTimeInformation(currentEnd, facilityTimezone);
      setNewEndTime(
        convertToUtcFormat({
          ...newTimeProps,
          year,
          month,
          date,
        })
      );
    }
  };

  // This cannot be outside the functional component or wrapped in React hooks
  // Otherwise Ionic Picker does not render properly when opened the second time
  // https://github.com/ionic-team/ionic-framework/issues/17664#issuecomment-649945238
  const columnsForTimePicker = [
    {
      name: "hour",
      options: generateTimePickerOptions(1, 12),
    },
    {
      name: "minute",
      options: generateTimePickerOptions(0, 59, 5),
    },
    {
      name: "meridiem",
      options: [{ text: Meridiem.AM }, { text: Meridiem.PM }],
    },
  ];

  return (
    <IonGrid>
      <IonRow className="time-stepper">
        <IonCol size="8">
          <IonRow>
            <IonText className="time-info-text">Start</IonText>
          </IonRow>
          <IonRow>
            <IonText
              className={`displayed-time ${currentStart !== newStartTime ? "green-text" : ""}`}
            >
              {startDisplayText}
            </IonText>
          </IonRow>
        </IonCol>
        <IonCol size="4" className="edit-text-column">
          <Button
            disabled={disabled}
            onClick={() => {
              openTimeEditor(ShiftTiming.START);
            }}
            aria-label="Edit Shift Start Time"
            sx={DEPRECATED_EDIT_SHIFT_TIME_BUTTON_SX}
          >
            Edit
          </Button>
        </IonCol>
      </IonRow>
      <IonRow className="time-stepper">
        <IonCol size="8">
          <IonRow>
            <IonText className="time-info-text">End</IonText>
          </IonRow>
          <IonRow>
            <IonText className={`displayed-time ${currentEnd !== newEndTime ? "green-text" : ""}`}>
              {endDisplayText}
            </IonText>
          </IonRow>
        </IonCol>
        <IonCol size="4" className="edit-text-column">
          <Button
            disabled={disabled}
            onClick={() => {
              openTimeEditor(ShiftTiming.END);
            }}
            aria-label="Edit Shift End Time"
            sx={DEPRECATED_EDIT_SHIFT_TIME_BUTTON_SX}
          >
            Edit
          </Button>
        </IonCol>
      </IonRow>

      <IonPicker
        buttons={[
          {
            text: "Cancel",
            role: "cancel",
          },
          {
            text: "Confirm",
            handler: handleTimeChange,
          },
        ]}
        columns={columnsForTimePicker}
        isOpen={showPicker}
        onDidDismiss={() => {
          setShowPicker(false);
        }}
        data-testid="time-picker"
      />
    </IonGrid>
  );
};
